import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';

/**
 * A hook that resolves a promise upon mounting.
 * @param invoke A function that returns a promise.
 * The AbortSignal is aborted upon unmounting.
 * This function is invoke again if it changes,
 *  and it is therefore recommended that the function is wrapped in useCallback.
 * @param initialValue An initial value that is returned before the promise is resolved
 */
const usePromise = <T>(
  invoke: (abort: AbortSignal) => Promise<T> | undefined,
  initialValue: T,
  setLoad: (loading: boolean) => void,
  defaultLoading = true,
): [T, React.Dispatch<React.SetStateAction<T>>, Error | undefined] => {
  const initialRef = useRef(initialValue);
  const loadingRef = useRef(defaultLoading);
  const [result, setResult] = useState<T>(initialValue);
  const [error, setError] = useState<Error | undefined>();

  /**
   * Resolves the promise
   */
  const resolve = useCallback(
    async (abort: AbortSignal) => {
      try {
        setError(undefined);
        if (!loadingRef.current) {
          setLoad(true);
        }
        // Get the promise from the function
        const promise = invoke(abort);
        if (!promise) setResult(initialRef.current);
        else {
          // Async resolve it
          const resolved = await promise;
          // Set the result
          setResult(resolved);
        }

        loadingRef.current = true;
      } catch (newError) {
        if (!abort.aborted && newError instanceof Error) {
          setError(newError);
        }
      } finally {
        if (!abort.aborted) {
          // setLoading(false);
          loadingRef.current = false;
          setLoad(false);
        }
      }
    },
    [invoke, setLoad],
  );

  useEffect(() => {
    const abort = new AbortController();
    // Resolve the promise on mount and when the resolve callback changes,
    // which it does when passed in dependencies changes
    resolve(abort.signal);
    return () => {
      abort.abort();
    };
  }, [resolve]);

  return [result, setResult, error];
};

export default usePromise;
